import React, { useRef, useEffect, memo } from "react";
import * as d3 from "d3";
import { ChevronLeft, ChevronRight, CirclePlay } from "lucide-react";
import { LineChartDataItemProps } from "./ChartTypes";
import {
  formatBandwidth,
  EpochToDateTime,
  getShadow360QueryBuilderURL,
} from "@utils/utils";
interface DonutChartProps {
  title?: string;
  parentid: string;
  data: LineChartDataItemProps[][];
  labels?: Array<string>;
  config?: any;
  onNodeClick?: (node: any) => void;
}

const COLORS = [
  "#1ad598",
  "#008e16",
  "#57baf1",
  "#1d69df",
  "#7618b1",
  "#7f57f1",
  "#a05195",
  "#9d3135",
  "#d45087",
  "#f95d6a",
  "#ff7c43",
  "#ffa600",
];

const TIME_MAP: any = {
  day: 1440,
  "3day": 4320,
  "7day": 10080,
  month: 43200,
  "2month": 86400,
};

const LineChart: React.FC<DonutChartProps> = memo(
  ({ title, parentid, data, labels = [], config, onNodeClick }) => {
    const zoom = window.innerWidth / 1920;
    const svgRef = useRef<SVGSVGElement>(null);
    const legendRef = useRef<HTMLDivElement>(null);
    const legendXScroll = useRef<HTMLDivElement>(null);
    // Set dimensions and margins
    const margin = {
      top: 20 * zoom,
      right: 10 * zoom,
      bottom: 80,
      left: 50 * zoom,
    };

    let smoothline = false;
    let showPoints = false;
    let timeSeries = false;
    let isS360Link = false;
    let S360_LINK = "";
    let legendPosition = "top";
    let legendSize = 0;
    let yFormat = "number";
    let xType = "number";
    let maxXticks = 10;
    let maxYticks = 10;
    let maxPoints = 200;

    if (config) {
      if (Object.prototype.hasOwnProperty.call(config, "smoothline")) {
        smoothline = config.smoothline;
      }
      if (Object.prototype.hasOwnProperty.call(config, "showPoints")) {
        showPoints = config.showPoints;
      }
      if (Object.prototype.hasOwnProperty.call(config, "timeSeries")) {
        timeSeries = config.timeSeries;
      }
      if (Object.prototype.hasOwnProperty.call(config, "legendPosition")) {
        legendPosition = config.legendPosition;
      }
      if (Object.prototype.hasOwnProperty.call(config, "yFormat")) {
        yFormat = config.yFormat;
      }
      if (Object.prototype.hasOwnProperty.call(config, "xType")) {
        xType = config.xType;
      }
      if (Object.prototype.hasOwnProperty.call(config, "maxXticks")) {
        maxXticks = config.maxXticks;
      }
      if (Object.prototype.hasOwnProperty.call(config, "maxYticks")) {
        maxYticks = config.maxYticks;
      }
      if (Object.prototype.hasOwnProperty.call(config, "maxPoints")) {
        maxPoints = config.maxPoints;
      }
      if (Object.prototype.hasOwnProperty.call(config, "s360Link")) {
        isS360Link = config.s360Link;
      }
    }

    if (isS360Link) {
      S360_LINK = getShadow360QueryBuilderURL({
        startTime: TIME_MAP[config.s360Date] || 1440,
        endTime: TIME_MAP[config.s360Date] || 1440,
        // startTime:  Math.floor(convertToEpochTime(config.s360Date.toString()) / 1000),
        // endTime: Math.floor(new Date().getTime() / 1000),
        logSrcs: ["linkflow"],
        fieldValObj: [
          { fieldName: "l7Protocol2", fieldValue: "__REPLACE_LABEL_HOLDER__" },
          { fieldName: "l7Protocol", fieldValue: "__REPLACE_LABEL_HOLDER__" },
        ],
        sensor: "master",
        operation: "is_equal",
      });
    }

    if (legendPosition == "right" || legendPosition == "left") {
      legendSize = 150;
    } else if (legendPosition == "bottom") {
      legendSize = 30;
    }

    if (timeSeries) {
      data.forEach((d: any) => {
        d.forEach((dd: any) => {
          let dt = new Date(dd.x);
          if (dd.x.toString().length <= 10) {
            dt = new Date(dd.x * 1000);
          }
          dd.x = dt.getTime();
        });
      });
    }

    function largestTriangleThreeBuckets(
      data: LineChartDataItemProps[],
      targetPoints: number,
    ) {
      if (data.length <= targetPoints) return data;
      data = data.sort((a, b) => a.x - b.x);
      const bucketSize = Math.floor(data.length / targetPoints);
      const downsampled = [data[0]]; // Start with the first point

      for (let i = 0; i < targetPoints - 2; i++) {
        const start = i * bucketSize;
        const end = start + bucketSize;
        const bucket = data.slice(start, end);

        // Find the point in the bucket with the largest area
        let maxArea = -1;
        let point = bucket[0];

        for (let j = 1; j < bucket.length - 1; j++) {
          const area = Math.abs(
            (bucket[j].x - bucket[0].x) *
              (bucket[j].y - bucket[bucket.length - 1].y) -
              (bucket[j].x - bucket[bucket.length - 1].x) *
                (bucket[0].y - bucket[j].y),
          );
          if (area > maxArea) {
            maxArea = area;
            point = bucket[j];
          }
        }
        downsampled.push(point);
      }

      downsampled.push(data[data.length - 1]); // End with the last point
      return downsampled;
    }

    const [excludedline, setExcludedline] = React.useState<number[]>([]);
    function drawLineChart(
      title: string,
      parentid: string,
      data: LineChartDataItemProps[][],
    ) {
      // Clear previous chart
      d3.select(svgRef.current).selectAll("*").remove();

      const parentElement = document.getElementById(parentid);
      if (!parentElement) {
        // Handle the error
        console.error(`No element with ID ${parentid}`);
        return;
      }

      const rect = parentElement.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      // Create SVG element
      const svg = d3
        .select(svgRef.current)
        .append("g")
        .attr("transform", `translate(${margin.left}, ${margin.top})`);

      let totaldata: LineChartDataItemProps[] = [];

      data.forEach((d: any, i) => {
        if (excludedline.includes(i)) {
          return;
        }
        totaldata = totaldata.concat(d);
      });

      totaldata.sort((a, b) => a.x - b.x);

      let maxX = d3.max(totaldata, (d) => d.x) as number;
      const minX = d3.min(totaldata, (d) => d.x) as number;

      // Set x and y scales
      let xScale: any;

      if (xType == "string") {
        xScale = d3
          .scalePoint()
          .domain(totaldata.map((d) => d.x.toString()))
          .rangeRound([
            0,
            width -
              margin.left -
              margin.right -
              (legendPosition == "right" || legendPosition == "left"
                ? legendSize
                : 0),
          ]);
      } else {
        xScale = d3
          .scaleTime()
          .domain([minX, maxX])
          .range([
            0,
            width -
              margin.left -
              margin.right -
              (legendPosition == "right" || legendPosition == "left"
                ? legendSize
                : 0),
          ]);
      }

      const yScale = d3
        .scaleLinear()
        .domain([0, d3.max(totaldata, (d) => d.y) as number])
        .range([
          height -
            margin.top -
            margin.bottom -
            (legendPosition == "bottom" ? legendSize : 0),
          0,
        ]);

      let maxY = d3.max(totaldata, (d) => d.y) as number;

      let dist = Math.ceil(maxY / maxYticks);
      if (dist > 50) {
        dist = Math.ceil(dist / 10) * 10;
      }
      if (maxY == 0) {
        maxY = 1;
        dist = 10;
      }

      const tickValues = d3.range(0, maxY + dist / 2, dist);

      if (minX == maxX) {
        maxX = maxX + 1;
      }

      const formatTime = d3.timeFormat("%I:%M %p");
      const formatDateTime = d3.timeFormat("%b %d");

      const DAY_MS = 24 * 60 * 60 * 1000;
      const uniqueXValues = Array.from(
        new Set(totaldata.map((point) => Number(point.x))),
      ).sort((a, b) => a - b);

      const hasSubDayGranularity = uniqueXValues.some(
        (value, idx) => idx > 0 && value - uniqueXValues[idx - 1] < DAY_MS,
      );

      const getDayKey = (value: number) => {
        const dt = new Date(value);
        return `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
      };

      const generatedTicks: Date[] =
        xType == "number" && timeSeries && typeof xScale.ticks === "function"
          ? xScale.ticks(maxXticks)
          : [];

      const customFormatter = (d: any, i: number) => {
        const currentTick = generatedTicks[i] ?? new Date(d);

        if (!hasSubDayGranularity) {
          return formatDateTime(currentTick);
        }

        if (i === 0) {
          return formatDateTime(currentTick);
        }

        const prevTick = generatedTicks[i - 1] ?? new Date(d);
        const currentDay = getDayKey(currentTick.getTime());
        const prevDay = getDayKey(prevTick.getTime());

        if (currentDay !== prevDay) {
          return formatDateTime(currentTick);
        }

        return formatTime(currentTick);
      };

      // Create x and y axes
      let xAxis: any = d3.axisBottom(xScale as any);

      if (timeSeries) {
        xAxis = d3
          .axisBottom(xScale as any)
          .ticks(maxXticks)
          .tickFormat(customFormatter);
      }

      const yAxis = d3
        .axisLeft(yScale)
        .tickValues(tickValues)
        .tickFormat((d: any) => {
          if (yFormat == "number") {
            if (d > 1000000000) {
              return (d / 1000000000).toFixed(2) + "B";
            } else if (d > 1000000) {
              return (d / 1000000).toFixed(2) + "M";
            } else if (d > 1000) {
              return (d / 1000).toFixed(2) + "K";
            } else {
              return d;
            }
          } else if (yFormat == "percent") {
            return d + "%";
          } else if (yFormat == "bandwidth") {
            return formatBandwidth(d, "B", 1);
          }
        });

      // Append x and y axes to the SVG
      svg
        .append("g")
        .attr(
          "transform",
          `translate(0, ${height - margin.top - margin.bottom - (legendPosition == "bottom" ? legendSize : 0)})`,
        )
        .call(xAxis)
        .attr("class", "xaxis")
        .selectAll("text")
        .style("font-size", "0.7rem")
        // .attr("transform", "rotate(-35) translate(-10, 10)")
        .style("text-anchor", "start");

      const xLabels = svg.selectAll(".xaxis>.tick>text");
      xLabels.each(function () {
        const txt = d3.select(this).text();
        if (txt.length == 8) {
          d3.select(this).attr(
            "transform",
            `translate(0, 0) rotate(-35) translate(${-50 * zoom}, 0)`,
          );
        } else {
          d3.select(this).attr(
            "transform",
            `translate(0, 0) rotate(-35) translate(${-38 * zoom}, 0)`,
          );
        }
      });

      svg
        .append("g")
        .call(yAxis)
        .attr("class", "yaxis")
        .selectAll("text")
        .style("fill", "var(--foreground)")
        .style("font-size", "0.7rem")
        .select(".domain")
        .style("stroke", "white");

      svg.selectAll("path").style("stroke", "none");
      svg.selectAll("text").style("fill", "var(--foreground)");

      // Create gridlines
      svg
        .selectAll("g.yaxis>.tick>line")
        .style("stroke", "var(--grid)")
        .attr("x2", width - legendSize - margin.right - margin.left)
        .style("stroke-width", 0.5);
      svg
        .selectAll("g.xaxis>.tick>line")
        .style("stroke", "var(--grid)")
        .attr("y2", -height + margin.top + margin.bottom)
        .style("stroke-width", 0.5);

      let i = 0;
      let count = 0;
      for (const ln of data) {
        if (xType == "number") {
          ln.sort((a, b) => a.x - b.x);
        }
        const line = d3
          .line<LineChartDataItemProps>()
          .x((d: any) => xScale(d.x) || 0)
          .y((d: any) => yScale(d.y) || 0);

        if (smoothline) {
          line.curve(d3.curveCardinal.tension(0.5));
        }

        // Append line to the SVG
        if (excludedline.includes(i) == false) {
          svg
            .append("path")
            .datum(ln)
            .attr("fill", "none")
            .attr("stroke", COLORS[i])
            .attr("stroke-width", 2 * zoom)
            .attr("d", line as any)
            .style("opacity", 0)
            .transition()
            .duration(500)
            .delay(100 * count)
            .style("opacity", 1);

          count++;
        }

        if (excludedline.includes(i) == false) {
          const gp = svg.selectAll(".allcircle").data(ln).enter().append("g");

          gp.append("text")
            .text((d: any) => {
              let labelValue = d.y;
              if (yFormat == "bandwidth") {
                labelValue = formatBandwidth(d.y, "B", 1);
              }
              if (timeSeries) {
                labelValue = labelValue + " : " + EpochToDateTime(d.x);
              }
              return labelValue;
            })
            .attr("x", (d: any) => xScale(d.x))
            .attr("y", (d: any) => yScale(d.y))
            .attr("text-anchor", "middle")
            .style("fill", "var(--background)")
            .style("font-size", "0.7rem")
            .style("font-weight", "bold")
            .style("opacity", 0)
            .style("pointer-events", "none")
            .attr("class", "tool_text")
            .attr("transform", "translate(0, -15)");

          // Background circle
          gp.append("circle")
            .attr("cx", (d: any) => xScale(d.x))
            .attr("cy", (d: any) => yScale(d.y))
            .attr("r", 6 * zoom)
            .attr("class", "bgcircle")
            .attr("fill", "var(--destructive)")
            .style("stroke", "var(--foreground)")
            .style("stroke-width", 1)
            .style("opacity", 0);

          //Foreground Circle
          gp.append("circle")
            .attr("cx", (d: any) => xScale(d.x))
            .attr("cy", (d: any) => yScale(d.y))
            .attr("r", 4 * zoom)
            .attr("fill", () => COLORS[i])
            .style("opacity", 0)
            .attr("class", "maincircle")
            .style("cursor", "pointer")
            // .attr("filter", "drop-shadow(0px 5px 5px rgba(0, 0, 0, 0.5))")
            .transition()
            .duration(1000)
            .delay((_d, i) => 500 + i * 100)
            .style("opacity", () => (showPoints ? 1 : 0));

          //Clicked Circle
          gp.append("circle")
            .attr("cx", (d: any) => xScale(d.x))
            .attr("cy", (d: any) => yScale(d.y))
            .attr("r", 10 * zoom)
            .attr("fill", "none")
            .attr("stroke", "var(--foreground)")
            .style("opacity", 0)
            .attr("class", "clickring");

          gp.on("mouseover", function () {
            const text = d3.select(this).select("text");
            const bgcircle = d3.select(this).select("circle.bgcircle");
            const maincircle = d3.select(this).select("circle.maincircle");

            maincircle
              .transition()
              .duration(200)
              .attr("r", 4 * zoom);

            bgcircle
              .transition()
              .duration(200)
              .attr("r", 6 * zoom)
              .style("opacity", 1);

            const textElement = text.node() as SVGTextElement;

            text.style("opacity", 1);
            const {
              x,
              y,
              width: textWidth,
              height: textHeight,
            } = textElement.getBBox();

            const tooltip = d3
              .select(this)
              .insert("g", ":first-child")
              .attr("class", "tooltip")
              .style("pointer-events", "none");

            tooltip
              .append("rect")
              .attr("x", x - 6)
              .attr("y", y - 18)
              .attr("width", textWidth + 12)
              .attr("height", textHeight + 6)
              .attr("fill", "var(--foreground)")
              .attr("rx", 2)
              .style("opacity", 0)
              .transition()
              .duration(200)
              .style("opacity", 1);
          })
            .on("mouseout", function () {
              const text = d3.select(this).select("text");
              text.style("opacity", 0);
              d3.select(this)
                .select("g.tooltip")
                .transition()
                .attr("opacity", 0)
                .remove();
              const bgcircle = d3.select(this).select("circle.bgcircle");

              if (!bgcircle.attr("class").includes("clicked")) {
                bgcircle
                  .transition()
                  .duration(200)
                  .attr("r", 4 * zoom)
                  .style("opacity", 0);
              }

              const maincircle = d3.select(this).select("circle.maincircle");

              maincircle
                .transition()
                .duration(200)
                .attr("r", 4 * zoom);
            })
            .on("click", function (d: any) {
              if (onNodeClick) {
                const bgcircle = d3.select(this).select("circle.bgcircle");
                const maincircle = d3.select(this).select("circle.maincircle");
                const ring = d3.select(this).select("circle.clickring");

                maincircle
                  .transition()
                  .duration(200)
                  .attr("r", 4 * zoom);

                if (!bgcircle.attr("class").includes("clicked")) {
                  d3.selectAll("circle.bgcircle")
                    .attr("class", "bgcircle")
                    .attr("r", 4 * zoom)
                    .style("opacity", 0);
                  d3.selectAll("circle.clickring")
                    .style("opacity", 0)
                    .attr("class", "clickring");

                  bgcircle
                    .transition()
                    .duration(200)
                    .attr("r", 6 * zoom)
                    .attr("class", "bgcircle clicked")
                    .style("opacity", 1);

                  ring
                    .style("opacity", 1)
                    .attr("class", "clickring animate-pulse");

                  onNodeClick(d);
                } else {
                  bgcircle
                    .transition()
                    .duration(200)
                    .attr("r", 4 * zoom)
                    .style("opacity", 0)
                    .attr("class", "bgcircle");

                  ring.style("opacity", 0).attr("class", "clickring");

                  onNodeClick(null);
                }
              }
            });
        }

        i++;
      }

      let allrect = document.getElementsByClassName("tool_rect");
      for (const rect of allrect) {
        const par = rect.parentElement;
        par?.appendChild(rect);
      }

      allrect = document.getElementsByClassName("tool_text");
      for (const rect of allrect) {
        const par = rect.parentElement;
        par?.appendChild(rect);
      }

      if (title) {
        svg
          .append("text")
          .text(title)
          .attr("x", 0)
          .attr("y", -40)
          .style("font-size", "1rem")
          .style("font-weight", "bold")
          .style("fill", "var(--foreground)");
      }
    }

    useEffect(() => {
      if (!svgRef.current) return;
      const smallData: LineChartDataItemProps[][] = [];
      data.forEach((d) => {
        const newdata = largestTriangleThreeBuckets(d, maxPoints);
        smallData.push(newdata);
      });
      drawLineChart(title ?? "", parentid, smallData);
    }, [data, excludedline]);

    function selectUnselectAll() {
      let extemp: number[] = [];
      if (excludedline.length == 0) {
        const childNodes = d3
          .select(legendRef.current)
          .selectAll(".divLeg")
          .nodes();
        childNodes.forEach((_, index: number) => {
          extemp.push(index);
        });
      } else {
        extemp = [];
      }
      setExcludedline([...extemp]);
    }

    function clickLegend(index: number) {
      const extemp: number[] = excludedline;
      if (extemp.includes(index)) {
        extemp.splice(extemp.indexOf(index), 1);
      } else {
        extemp.push(index);
      }
      setExcludedline([...extemp]);
    }

    function s360linkNav(label: string) {
      S360_LINK = S360_LINK.replaceAll("__REPLACE_LABEL_HOLDER__", label);
      window.open(S360_LINK, "_blank");
    }

    const scrollButtons = (
      <div
        className={
          "absolute right-0 " +
          (legendPosition == "top" ? "top-0 -mt-8" : "bottom-0")
        }
      >
        <ChevronLeft
          size={18}
          className="rounded-sm inline-block border-2 me-4 cursor-pointer"
          onClick={() => {
            if (legendXScroll.current) {
              legendXScroll.current.scrollBy({
                left: -100,
                top: 0,
                behavior: "smooth",
              });
            }
          }}
        />
        <ChevronRight
          size={18}
          className="rounded-sm inline-block border-2 cursor-pointer"
          onClick={() => {
            if (legendXScroll.current) {
              legendXScroll.current.scrollBy({
                left: 100,
                top: 0,
                behavior: "smooth",
              });
            }
          }}
        />
      </div>
    );

    return (
      <div
        className={
          "flex h-full relative " +
          (legendPosition == "top" || legendPosition == "bottom"
            ? "flex-col"
            : "")
        }
      >
        {legendPosition == "top" && labels.length > 0 && scrollButtons}
        {(legendPosition == "bottom" || legendPosition == "right") && (
          <svg ref={svgRef} className="overflow-visible flex-1"></svg>
        )}
        {legendPosition == "bottom" && labels.length > 0 && scrollButtons}

        {labels.length > 0 && (
          <div
            ref={legendRef}
            className={
              "text-xs overflow-x-auto whitespace-nowrap no-scrollbar " +
              (legendPosition == "left"
                ? "pe-2"
                : legendPosition == "right" || legendPosition == "left"
                  ? "h-full"
                  : legendPosition == "bottom"
                    ? ""
                    : "")
            }
          >
            <div
              ref={legendXScroll}
              className={
                "overflow-y-auto no-scrollbar " +
                (legendPosition == "right" || legendPosition == "left"
                  ? "max-w-40 h-full justify-center"
                  : legendPosition == "bottom"
                    ? "pt-2 flex"
                    : "flex")
              }
            >
              <div
                className={
                  "inline-block me-4 cursor-pointer mb-2 " +
                  (legendPosition == "right" || legendPosition == "left"
                    ? "text-ellipsis whitespace-nowrap overflow-hidden"
                    : "")
                }
                onClick={() => selectUnselectAll()}
              >
                <div className="me-2 h-3 w-3 rounded-sm inline-block bg-secondary"></div>
                <span>Select/Unselect All</span>
              </div>

              {labels.map((label: string, index: number) => (
                <div
                  key={index}
                  className={
                    "mb-2 divLeg cursor-pointer flex items-center group " +
                    (legendPosition == "right" || legendPosition == "left"
                      ? "text-ellipsis whitespace-nowrap overflow-hidden "
                      : "me-4 ") +
                    (excludedline.includes(index) ? "opacity-50" : "")
                  }
                  onClick={() => clickLegend(index)}
                >
                  <div
                    className="w-3 h-3 mr-2 rounded-sm inline-block"
                    style={{ backgroundColor: COLORS[index] }}
                  ></div>
                  <span>{label}</span>

                  {isS360Link && (
                    <button
                      type="button"
                      title="Search in Shadow360"
                      className="ml-2 inline-flex items-center opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        s360linkNav(label);
                      }}
                    >
                      <CirclePlay size={14} className="text-primary" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {(legendPosition == "top" || legendPosition == "left") && (
          <svg ref={svgRef} className="overflow-visible flex-1"></svg>
        )}
      </div>
    );
  },
  arePropsEqual,
);

function arePropsEqual(prevProps: any, nextProps: any) {
  const prevTotal = prevProps.data.reduce((acc: any, curr: any) => {
    return acc + curr.y;
  }, 0);
  const newTotal = nextProps.data.reduce((acc: any, curr: any) => {
    return acc + curr.y;
  }, 0);

  if (prevTotal !== newTotal) return false;

  return true;
}

LineChart.displayName = "LineChart";

export default LineChart;
